import React, { useMemo } from 'react';
import { injectStripe } from 'react-stripe-elements';
import 'Components/Modals/PaymentModal/index.css';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import {
  FDAlert,
  FDBackButton,
  FDBox,
  FDPaper,
  FDContentContainer,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { buildBuyWizNav } from 'Components/ClientApp/CombinedApp/Navigation/TopNav/BuyWizNav';
import TopNav from 'Components/ClientApp/CombinedApp/Navigation/TopNav';
import useMobileLayout from 'Hooks/useMobileLayout';
import useBackgroundColor from 'Hooks/useBackgroundColor';
import { dataAttrs } from 'Helpers/Testing';
import ErrorList from 'Components/UI/ErrorList';

import segmentEvents from 'EventTracking/consts/segmentEvents';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { useLeadPropsContext } from 'Context/LeadPropsContext';
import { LeadPromosContainer } from './LeadPromosContainer';
import { useSubmitHandler } from './useSubmitHandler';

const useStyles = buildUseStyles('PaymentStepLayout', (theme) => ({
  root: {
    '& *:not(a, button, button *, [color], [data-color], [test="error-list"] *, h3, h5, [data-element-semantic="member agreement"] span:nth-of-type(1) *, [data-element-semantic="member agreement"] span:nth-of-type(1), [data-element-semantic="terms and conditions"] span:nth-of-type(1) *, [data-element-semantic="terms and conditions"] span:nth-of-type(1))': {
      // Super temporary hack.
      // In the future, this color (or whatever the right color is) will make its way into the design system tokens, and then be used in typography.
      // But for now, typography doesn't look like this, and adjusting each thing means you now have a lot more to adjust after the design system updates.
      color: '#003E54 !important',
    },
  },
  // I need FDPaper to have display:flex so that it can pass along the height of the other FDPaper.
  // This class handles that, and since I'm adding a div in between to be able to apply this class, I also had to make it display:flex and flex:1 to pass that flex:1 value along.
  // Without this class, if the left doesn't have content as tall as the right, the Total won't appear at the bottom of the paper - there will be some whitespace below the total
  flexPaper: {
    display: 'flex',
    flex: 1,
    '& > *': {
      display: 'flex',
    },
  },
  '& h4': {
    lineHeight: `${theme.spacing(4)}px`,
  },
}));

const PaymentStepLayout = ({ stripe }) => {
  const { pageContentResource } = usePageContentContext();
  const { logEvent } = useEventTrackingLogEvent({ nodeName: 'payment' });
  const leadProps = useLeadPropsContext();

  const {
    rootNodeProps,
    onSubmit,
    metaErrors,
    stripeError,
    loading,
    disabled,
  } = useSubmitHandler({
    stripe,
    onSubmitEvent: () => logEvent(segmentEvents.leadSubmittedPaymentStep, leadProps),
    onErrorEvent: () => logEvent(segmentEvents.leadPaymentError, leadProps),
  });
  const isMobile = useMobileLayout();
  const theme = useTheme();
  const classes = useStyles();
  useBackgroundColor(theme.palette.facet.lightGray);

  const backButton = pageContentResource.withRelationship(
    fr.relationships.previousLink.name,
    (rel) => (
      <FDBackButton
        onClick={() => {
          // Can't use something like useHistory.push because that won't change the domain, and can't use something like href= because that would open a new tab, and in this particular case, that is no bueno
          window.location = rel.link('related').url();
        }}
      />
    ),
    () => null,
  );


  const needHelp = pageContentResource.withRelationship(
    fr.relationships.needHelp.name,
    (rel) => {
      return (
        <FDSecondaryActionButton
          href={rel.link('related').attr('url')}
          label="Need help?"
          icon="chatBubbleOutline"
          iconPosition="start"
          size="sm"
        />
      );
    },
    () => null,
  );

  const homePage = pageContentResource.withRelationship(
    fr.relationships.homePage.name,
    (rel) => rel.link('related').url(),
    () => undefined,
  );
  const BuyWizNav = useMemo(() => buildBuyWizNav({ homePage }), [homePage]);

  let error = null;
  if (stripeError) {
    error = (
      <FDAlert
        title="Error processing credit card"
        description={stripeError}
        status="error"
        {...dataAttrs('stripe error')}
      />
    );
  } else if (metaErrors.length) {
    error = <ErrorList errors={metaErrors} title="Error" hideErrorTitle />;
  }

  return (
    <>
      {!isMobile && <TopNav Component={BuyWizNav} />}
      <div {...rootNodeProps} className={classes.root}>
        <FDContentContainer maxWidth="md">
          <FDBox paddingY={3}>
            <FDStack spacing={3} direction="column">
              <MetaControlField attr="moduleName" />
              <FDStack wrap spacing={2}>
                <div className={classes.flexPaper}>
                  <FDPaper appearance="elevated" overflow="hidden" minWidth={theme.dimensions.sm} flex={1}>
                    <FDStack direction="column" justifyContent="space-between" flexGrow={1}>
                      <FDBox padding={4}>
                        <FDStack direction="column" spacing={3}>
                          <FDStack justifyContent="space-between" alignItems="center" wrap>
                            <MetaControlField attr="paymentTitle" />
                            {needHelp}
                          </FDStack>
                          <FDStack direction="column" spacing={1}>
                            <MetaControlField attr="lineItems" />
                            <MetaControlField attr={'paymentInterval'} />
                            {pageContentResource.withRelationship(
                              fr.relationships.promos.name,
                              (rel) => (<LeadPromosContainer relationshipLink={rel} />),
                              () => null,
                            )}
                          </FDStack>
                        </FDStack>
                      </FDBox>
                      <MetaControlField attr="dueNow" {...dataAttrs('due now')} />
                    </FDStack>
                  </FDPaper>
                </div>
                <FDPaper padding={4} appearance="elevated" flex={1} minWidth={theme.dimensions.sm}>
                  <FDBox flexGrow={1}>
                    <FDStack direction="column" spacing={2}>
                      <MetaControlField attr="stripeToken" />
                      {!isMobile ? error : null}
                    </FDStack>
                  </FDBox>
                </FDPaper>
              </FDStack>
              <FDPaper padding={4} appearance="elevated">
                <FDStack direction="column" spacing={2} flex={1}>
                  <MetaControlField attr="agreementTitle" />
                  <MetaControlField attr="memberAgreement" {...dataAttrs('member agreement')} />
                  <MetaControlField attr="termsAndConditions" {...dataAttrs('terms and conditions')} />
                </FDStack>
              </FDPaper>
              <FDStack direction="column" spacing={2}>
                {isMobile ? error : null}
                <FDStack spacing={1} justifyContent="center">
                  {backButton}
                  <FDPrimaryActionButton
                    loading={loading}
                    onClick={onSubmit}
                    disabled={disabled}
                    label="Make Payment"
                    size="lg"
                    {...dataAttrs('continue-btn')}
                  />
                </FDStack>
              </FDStack>
            </FDStack>
            {/* IMPORTANT: do not try to use MetaFormFields here, as some fields are being purposefully hidden in certain circumstances to match the designs */}
          </FDBox>
        </FDContentContainer>
      </div>
    </>
  );
};

PaymentStepLayout.propTypes = {
  stripe: PropTypes.object.isRequired,
};

export default injectStripe(PaymentStepLayout);
